import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import spotlightService from '../services/SpotlightService';
import ChargifyPaymentForm from '../components/ChargifyPaymentForm';
import Header from '../components/common/Header';
import { mapV2PaymentInfo } from '../utils/paymentCard';
import './PaymentProfilePage.css';

const PaymentProfilePage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loginWithRedirect } = useAuth0();
  
  const [loading, setLoading] = useState(true);
  const [paymentProfile, setPaymentProfile] = useState(null);
  const [showUpdateForm, setShowUpdateForm] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [updateError, setUpdateError] = useState(null);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      loginWithRedirect({
        appState: {
          returnTo: '/account/payment',
        },
      });
    }
  }, [isAuthenticated, loginWithRedirect]);

  // Local storage key for payment profile cache
  const PAYMENT_PROFILE_CACHE_KEY = 'electricnow_payment_profile';

  // Fetch payment profile data
  useEffect(() => {
    const fetchPaymentProfile = async () => {
      if (!isAuthenticated) return;

      try {
        setLoading(true);
        console.log('💳 PaymentProfilePage: Fetching payment profile...');

        // Show a cached profile immediately (user may have added a card but not purchased)
        const cachedProfile = localStorage.getItem(PAYMENT_PROFILE_CACHE_KEY);
        if (cachedProfile) {
          try {
            setPaymentProfile(JSON.parse(cachedProfile));
          } catch (e) {
            localStorage.removeItem(PAYMENT_PROFILE_CACHE_KEY);
          }
        }

        // Authoritative read from the v2 products endpoint
        const v2 = await spotlightService.getCustomerProductsV2();
        const profile = mapV2PaymentInfo(v2?.payment_info);

        if (profile) {
          setPaymentProfile(profile);
          localStorage.setItem(PAYMENT_PROFILE_CACHE_KEY, JSON.stringify(profile));
          console.log('💳 PaymentProfilePage: Payment profile loaded:', profile);
        } else if (!cachedProfile) {
          console.log('💳 PaymentProfilePage: No card on file');
          setPaymentProfile(null);
        }
      } catch (error) {
        console.error('💳 PaymentProfilePage: Error fetching payment profile:', error);
        if (!localStorage.getItem(PAYMENT_PROFILE_CACHE_KEY)) {
          setPaymentProfile(null);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentProfile();
  }, [isAuthenticated]);

  // Handle adding new payment method
  const handleAddPayment = async (chargifyToken) => {
    console.log('💳 PaymentProfilePage: Adding new payment method...');
    setIsUpdating(true);
    setUpdateError(null);

    try {
      const tokenData = chargifyToken;
      const firstName = tokenData.firstName || 'Customer';
      const lastName = tokenData.lastName || 'User';
      const token = tokenData.token || tokenData;

      // Create payment profile using the correct endpoint
      const response = await spotlightService.callAPI('subscriptions/users/create_with_payment', null, {
        method: 'POST',
        body: JSON.stringify({
          chargify_token: token,
          first_name: firstName,
          last_name: lastName,
        }),
        headers: { 'Content-Type': 'application/json' },
        requiresClientAuth: true,
        skipCache: true,
      });

      console.log('✅ Payment method added:', response);
      
      setUpdateSuccess(true);
      setShowAddForm(false);

      // Create the payment profile object
      const newProfile = {
        card_type: response?.card_type || 'card',
        last_four: (response?.masked_card_number || '').slice(-4) || '****',
        expiration_month: response?.expiration_month || '',
        expiration_year: response?.expiration_year || '',
      };

      // Set the payment profile with the new card info
      setPaymentProfile(newProfile);
      
      // Cache in localStorage so it persists across refreshes
      localStorage.setItem(PAYMENT_PROFILE_CACHE_KEY, JSON.stringify(newProfile));
      console.log('💳 PaymentProfilePage: Payment profile cached to localStorage');

      // Clear success message after 3 seconds
      setTimeout(() => {
        setUpdateSuccess(false);
      }, 3000);

    } catch (error) {
      console.error('❌ Payment add failed:', error);
      setUpdateError(error.message || 'Failed to add payment method. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle payment update
  const handleUpdatePayment = async (chargifyToken) => {
    console.log('💳 PaymentProfilePage: Updating payment method...');
    setIsUpdating(true);
    setUpdateError(null);

    try {
      const tokenData = chargifyToken;
      const firstName = tokenData.firstName || 'Customer';
      const lastName = tokenData.lastName || 'User';
      const token = tokenData.token || tokenData;

      const response = await spotlightService.callAPI('subscriptions/payment/update', null, {
        method: 'PUT',
        body: JSON.stringify({
          chargify_token: token,
          first_name: firstName,
          last_name: lastName,
        }),
        headers: { 'Content-Type': 'application/json' },
        requiresClientAuth: true,
        skipCache: true,
      });

      console.log('✅ Payment method updated:', response);
      
      setUpdateSuccess(true);
      setShowUpdateForm(false);

      // Update the profile immediately with available info
      const updatedProfile = {
        card_type: response?.card_type || paymentProfile?.card_type || 'card',
        last_four: (response?.masked_card_number || '').slice(-4) || '****',
        expiration_month: response?.expiration_month || '',
        expiration_year: response?.expiration_year || '',
      };
      setPaymentProfile(updatedProfile);
      localStorage.setItem(PAYMENT_PROFILE_CACHE_KEY, JSON.stringify(updatedProfile));

      // Refresh payment profile data from API
      setTimeout(async () => {
        try {
          const v2 = await spotlightService.getCustomerProductsV2();
          const freshProfile = mapV2PaymentInfo(v2?.payment_info);
          if (freshProfile) {
            setPaymentProfile(freshProfile);
            localStorage.setItem(PAYMENT_PROFILE_CACHE_KEY, JSON.stringify(freshProfile));
          }
        } catch (e) {
          console.warn('💳 PaymentProfilePage: Could not refresh profile from API:', e);
        }
        setUpdateSuccess(false);
      }, 2000);

    } catch (error) {
      console.error('❌ Payment update failed:', error);
      setUpdateError(error.message || 'Failed to update payment method. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCancelUpdate = () => {
    setShowUpdateForm(false);
    setUpdateError(null);
  };

  const handleCancelAdd = () => {
    setShowAddForm(false);
    setUpdateError(null);
  };

  const handleUpdateError = (error) => {
    console.error('❌ Chargify error:', error);
    setUpdateError('Payment form error. Please check your card details and try again.');
  };

  if (!isAuthenticated) {
    return null;
  }

  if (loading) {
    return (
      <div className="payment-profile-page">
        <Header />
        <div className="payment-profile-container">
          <div className="payment-profile-loading">
            <div className="spinner"></div>
            <p>Loading payment information...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-profile-page">
      <Header />
      <div className="payment-profile-container">
        {/* Header */}
        <div className="payment-profile-header">
          <button onClick={() => navigate(-1)} className="btn-back-header">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="icon-back"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
                clipRule="evenodd"
              />
            </svg>
            Back
          </button>
          <h1>Payment Method</h1>
          <p className="payment-profile-subtitle">
            Manage your payment information for faster checkouts
          </p>
        </div>

        {/* Success Message */}
        {updateSuccess && (
          <div className="update-success-banner">
            <span className="success-icon">✓</span>
            <div>
              <strong>Payment Method Updated</strong>
              <p>Your payment information has been successfully updated.</p>
            </div>
          </div>
        )}

        {/* Error Message */}
        {updateError && (
          <div className="update-error-banner">
            <span className="error-icon">⚠️</span>
            <div>
              <strong>Update Failed</strong>
              <p>{updateError}</p>
            </div>
          </div>
        )}

        {/* Payment Profile Content */}
        <div className="payment-profile-content">
          {!paymentProfile && !showAddForm ? (
            // No payment profile - show option to add
            <div className="no-payment-profile">
              <div className="no-profile-icon">💳</div>
              <h2>No Payment Method on File</h2>
              <p>Add a payment method now for faster checkouts when purchasing content.</p>
              <div className="no-profile-actions">
                <button 
                  className="btn-add-payment"
                  onClick={() => setShowAddForm(true)}
                >
                  Add Payment Method
                </button>
                <button 
                  className="btn-browse-secondary"
                  onClick={() => navigate('/')}
                >
                  Browse Content
                </button>
              </div>
            </div>
          ) : showAddForm ? (
            // Show add payment form
            <div className="payment-update-form">
              <h2>Add Payment Method</h2>
              <p className="update-form-subtitle">
                Enter your card details below. This will be saved securely for future purchases.
              </p>

              <ChargifyPaymentForm
                onSuccess={handleAddPayment}
                onError={handleUpdateError}
                onCancel={handleCancelAdd}
                isUpdating={isUpdating}
                buttonText="Save Card"
                processingText="Saving..."
              />
            </div>
          ) : !showUpdateForm ? (
            // Show current payment method
            <div className="payment-profile-card">
              <div className="card-header">
                <h2>Current Payment Method</h2>
                <button 
                  className="btn-update"
                  onClick={() => setShowUpdateForm(true)}
                >
                  Update Card
                </button>
              </div>

              <div className="card-details">
                <div className="card-visual">
                  <div className="card-type-badge">
                    {paymentProfile.card_type?.toUpperCase() || 'CARD'}
                  </div>
                  <div className="card-number">
                    •••• •••• •••• {paymentProfile.last_four || '****'}
                  </div>
                  <div className="card-info">
                    <div className="card-expiry">
                      Expires {paymentProfile.expiration_month || '--'}/{paymentProfile.expiration_year || '--'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            // Show update form
            <div className="payment-update-form">
              <h2>Update Payment Method</h2>
              <p className="update-form-subtitle">
                Enter your new card details below. Your old payment method will be removed.
              </p>

              <ChargifyPaymentForm
                onSuccess={handleUpdatePayment}
                onError={handleUpdateError}
                onCancel={handleCancelUpdate}
                isUpdating={isUpdating}
                buttonText="Update Card"
                processingText="Updating..."
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentProfilePage;

